/**
 * DonationDialog (T35.7.2 Part A → .2 checkout wiring) — the "support development"
 * donation dialog.
 *
 * Composes `SubscriptionPicker` (the agnostic amount picker) with the donation-
 * specific framing: the "Support our development" header + the budget copy +
 * the disclaimer. Records a general donation (`type: 'donation'`) via
 * `/api/subscriptions/` (no idea).
 *
 * Flow (T35.7.2.2): record → `createSession` → `<PaymentHandoff>`. The stub has
 * no card step (`/session` finalizes synchronously) → immediate success. Stripe
 * mounts the Payment Element; the SUCCESS message fires the moment the charge
 * goes through (`onResult(true)`) — NOT after a webhook. The webhook still updates
 * Settings → Billing in the background, but this dialog never waits on it.
 *
 * Phase machine: `pick → pay → (success | failed)`. `failed` shows the universal
 * "didn't go through — you weren't charged" message with a `Try again` that
 * returns to `pick`; the reason (a Stripe decline message, a generic GPay reason,
 * or none) comes from the payment surface or a `createSession` throw.
 *
 * Owned by the subscriptions module.
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Modal } from '../../components/Modal';
import { SubscriptionPicker, subscriptionSuffix } from './SubscriptionPicker';
import { PaymentHandoff } from './paymentRenderAdapters';
import { PaymentSuccess, PaymentFailure } from './PaymentOutcome';
import {
  donate,
  createSession,
  abandonSubscription,
  type RecurringSchedule,
  type SessionDescriptor,
} from './api';

const budgetStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  margin: '0 0 var(--space-3)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const footStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 1.45,
};

type Phase = 'pick' | 'pay' | 'success' | 'failed';

export function DonationDialog({
  open,
  busy,
  setBusy,
  onClose,
  onConfirmed,
}: {
  open: boolean;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onClose: () => void;
  onConfirmed: (amount: number, suffix: string) => void;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('pick');
  const [descriptor, setDescriptor] = useState<SessionDescriptor | null>(null);
  const [failReason, setFailReason] = useState<string | undefined>(undefined);
  // The intent row created this attempt, so the failure/cancel path can abandon
  // it (BUG34) — clearing the never-charged row off Settings → Subscriptions.
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhase('pick');
      setDescriptor(null);
      setFailReason(undefined);
      setSubscriptionId(null);
    }
  }, [open]);

  async function confirmDonation(amountCents: number, recurring: RecurringSchedule): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailReason(undefined);
    let createdId: string | null = null;
    try {
      const sub = await donate(amountCents, recurring);
      createdId = sub.id;
      setSubscriptionId(sub.id);
      const session = await createSession(sub.id);
      setDescriptor(session);
      if (session.primary.processor === 'stub') {
        // The stub has no card step — `/session` already finalized the row.
        // Charge succeeded → success message + fire the parent side-effect.
        onConfirmed(Math.round(amountCents / 100), subscriptionSuffix(recurring));
        setPhase('success');
        return;
      }
      setPhase('pay');
    } catch {
      // record OR createSession threw — the payment never started, so the user
      // was NOT charged. Abandon the intent row if one was created (BUG34) so it
      // never lingers in Settings, then show the failure message.
      if (createdId) void abandonSubscription(createdId);
      setPhase('failed');
      setFailReason(undefined);
    } finally {
      setBusy(false);
    }
  }

  function onPaymentResult(ok: boolean, reason?: string): void {
    if (ok) {
      // The charge went through. Fire the parent's success side-effect, then show
      // the universal thank-you — immediately, never waiting on the webhook.
      onConfirmed(0, '');
      setPhase('success');
    } else {
      // Declined / errored at the processor — not charged. Abandon the intent row
      // (BUG34) so it leaves Settings immediately, then carry the reason.
      if (subscriptionId) void abandonSubscription(subscriptionId);
      setFailReason(reason);
      setPhase('failed');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Support our development"
      ariaLabel="Support our development"
      variant="sheet"
    >
      <div data-testid="donation-dialog">
        {phase === 'success' ? (
          <PaymentSuccess testIdPrefix="donation" onDone={onClose} />
        ) : phase === 'failed' ? (
          <PaymentFailure
            testIdPrefix="donation"
            reason={failReason}
            onRetry={() => {
              setFailReason(undefined);
              setPhase('pick');
            }}
          />
        ) : phase === 'pay' && descriptor ? (
          <PaymentHandoff descriptor={descriptor} onResult={onPaymentResult} />
        ) : (
          <>
            <p style={budgetStyle}>
              <span aria-hidden="true">ⓘ</span>
              <span data-testid="donation-budget">
                Back any amount you like — it goes straight to building Shopista and keeping a small
                team independent. Thank you.
              </span>
            </p>

            <SubscriptionPicker
              open={open}
              confirmLabel="Support"
              busy={busy}
              onClose={onClose}
              onConfirm={confirmDonation}
            />

            <p style={footStyle}>
              <span aria-hidden="true">✓ </span>
              A donation toward development — change or stop anytime. It&apos;s private to your
              household.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
